// La capa que toca el DOM: aplica un branding a la página, lo recuerda en el dispositivo
// y avisa a React (`useBranding`). Estado único de módulo: hay un solo branding activo.
//
// Orden de arranque: el script inline de `index.html` pinta el snapshot recordado antes
// del primer frame; `iniciarBranding` (main.tsx) lo vuelve a aplicar desde el catálogo
// (por si el snapshot quedó viejo) y toma `?branding=`; y cuando la sesión resuelve,
// `aplicarBrandingDeEmpresa` aplica lo que dice `company_profiles.branding`.

import {
  ATRIBUTO_DE_BRANDING,
  CLAVE_DE_BRANDING_EN_DISPOSITIVO,
  CLAVE_DE_BRANDING_FORZADO,
  snapshotDeBranding,
  variablesCssDe,
  type SnapshotDeBranding,
} from './arranque';
import { BRANDING_ATLAS, brandingPorSlug, SLUG_ATLAS, type Branding, type LogosDeBranding } from './catalogo';
import { resolverSlug, slugForzadoDesdeUrl } from './resolver';

export type VarianteDeLogo = 'solo' | 'soloBlanco' | 'horizontal' | 'horizontalBlanco';

let actual: Branding = BRANDING_ATLAS;
const oyentes = new Set<() => void>();

export function brandingActual(): Branding {
  return actual;
}

export function suscribirBranding(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

/** Todas las variables que un branding puede escribir: se borran antes de pintar el siguiente. */
const NOMBRES_DE_VARIABLES = Object.keys(variablesCssDe(BRANDING_ATLAS.colores));

function pintar(b: Branding): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  const snapshot = snapshotDeBranding(b);
  // Atlas no escribe nada inline: manda `:root` de index.css, que es lo de siempre.
  for (const nombre of NOMBRES_DE_VARIABLES) html.style.removeProperty(nombre);
  for (const [nombre, valor] of Object.entries(snapshot.variables)) html.style.setProperty(nombre, valor);
  if (b.slug === SLUG_ATLAS) html.removeAttribute(ATRIBUTO_DE_BRANDING);
  else html.setAttribute(ATRIBUTO_DE_BRANDING, b.slug);
  document.title = snapshot.titulo;
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => link.setAttribute('href', snapshot.favicon));
  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest) manifest.setAttribute('href', snapshot.manifest);
}

function recordar(b: Branding): void {
  try {
    localStorage.setItem(CLAVE_DE_BRANDING_EN_DISPOSITIVO, JSON.stringify(snapshotDeBranding(b)));
  } catch {
    // Sin storage (modo privado, cuota llena): la próxima carga arranca con Atlas y React corrige.
  }
}

function slugRecordado(): string | null {
  try {
    const crudo = localStorage.getItem(CLAVE_DE_BRANDING_EN_DISPOSITIVO);
    if (!crudo) return null;
    const snapshot = JSON.parse(crudo) as Partial<SnapshotDeBranding>;
    return typeof snapshot.slug === 'string' ? snapshot.slug : null;
  } catch {
    return null;
  }
}

/**
 * El `?branding=` de esta carga, también en memoria: sin sessionStorage (bloqueado o en
 * modo privado) es lo único que lo sostiene, y sin esto la lectura de la empresa lo pisaba.
 */
let forzadoDeEstaCarga: string | null = null;

function slugForzado(): string | null {
  try {
    return sessionStorage.getItem(CLAVE_DE_BRANDING_FORZADO) ?? forzadoDeEstaCarga;
  } catch {
    return forzadoDeEstaCarga;
  }
}

export function aplicarBranding(b: Branding): void {
  actual = b;
  pintar(b);
  recordar(b);
  oyentes.forEach((oyente) => oyente());
}

/**
 * Al arrancar, antes del primer render de React. Toma `?branding=<slug>` de la URL y lo
 * pega a la pestaña (previsualización, y el link que le damos al cliente); si no, aplica
 * lo recordado en el dispositivo, así la pantalla de login ya sale con la marca.
 */
export function iniciarBranding(): void {
  let forzado = slugForzado();
  const deUrl = slugForzadoDesdeUrl(window.location.search);
  if (deUrl) {
    forzado = deUrl;
    forzadoDeEstaCarga = deUrl;
    try {
      sessionStorage.setItem(CLAVE_DE_BRANDING_FORZADO, deUrl);
    } catch {
      // Sin sessionStorage el forzado vale sólo para esta carga (queda en memoria).
    }
  }
  aplicarBranding(brandingPorSlug(resolverSlug({ conSesion: false, forzado, recordado: slugRecordado() })));
}

/** Con la sesión resuelta: lo que dice `company_profiles.branding` de la empresa (`null` = Atlas). */
export function aplicarBrandingDeEmpresa(deEmpresa: string | null | undefined): void {
  aplicarBranding(
    brandingPorSlug(resolverSlug({ conSesion: true, forzado: slugForzado(), deEmpresa: deEmpresa ?? null })),
  );
}

export function rutaDeLogo(logos: LogosDeBranding, variante: VarianteDeLogo): string {
  if (variante === 'horizontalBlanco') return logos.horizontalBlanco ?? logos.soloBlanco;
  return logos[variante];
}

/** La ruta de un logo del branding activo, para `onError` y otros usos fuera de React. */
export function logoDeBranding(variante: VarianteDeLogo): string {
  return rutaDeLogo(actual.logos, variante);
}
